import { createContext, useContext } from 'react'
import type { PropsWithChildren } from 'react'

import { PlannedBreakFragment } from 'generated/resources/graphql'
import groupBy from 'lodash/groupBy'
import { computed, makeObservable } from 'mobx'

import PlannedBreak from 'modules/time_planning/store/internal/models/PlannedBreak'

class PlannedBreaks {
  plannedBreaks: PlannedBreak[]

  constructor(plannedBreaks: PlannedBreak[]) {
    this.plannedBreaks = plannedBreaks

    makeObservable(this, {
      active: computed,
      _groupedByDefaultShift: computed,
      _groupedByShift: computed,
      _groupedByDayConfiguration: computed,
      _groupedByShiftConfiguration: computed,
    })
  }

  static buildFromRequest(plannedBreaks: PlannedBreakFragment[]): PlannedBreaks {
    const breaksObjects = plannedBreaks.map((plannedBreak) => new PlannedBreak(plannedBreak))

    return new PlannedBreaks(breaksObjects)
  }

  get active(): PlannedBreaks {
    const activeBreaks = this.plannedBreaks.filter((plannedBreak) => plannedBreak.isActive)

    return new PlannedBreaks(activeBreaks)
  }

  get _groupedByDefaultShift(): Record<string, PlannedBreak[]> {
    return groupBy(this.plannedBreaks, (plannedBreak) => plannedBreak.defaultShiftId)
  }

  get _groupedByShift(): Record<string, PlannedBreak[]> {
    return groupBy(this.plannedBreaks, (plannedBreak) => plannedBreak.shiftId)
  }

  get _groupedByDayConfiguration(): Record<string, PlannedBreak[]> {
    return groupBy(this.plannedBreaks, (plannedBreak) => plannedBreak.dayConfigurationId)
  }

  get _groupedByShiftConfiguration(): Record<string, PlannedBreak[]> {
    return groupBy(this.plannedBreaks, (plannedBreak) => plannedBreak.shiftConfigurationId)
  }

  filterByDefaultShift(defaultShiftId: string): PlannedBreak[] {
    return this._groupedByDefaultShift[defaultShiftId] || []
  }

  filterByShift(shiftId: string): PlannedBreak[] {
    return this._groupedByShift[shiftId] || []
  }

  filterByShifts(shiftIds: string[]): PlannedBreak[] {
    return shiftIds.flatMap((id) => this.filterByShift(id))
  }

  filterByDayConfiguration(dayConfigurationId: string): PlannedBreak[] {
    return this._groupedByDayConfiguration[dayConfigurationId] || []
  }

  filterByDayConfigurations(dayConfigurationIds: string[]): PlannedBreak[] {
    return dayConfigurationIds.flatMap((id) => this.filterByDayConfiguration(id))
  }

  filterByShiftConfiguration(shiftConfigurationId: string): PlannedBreak[] {
    return this._groupedByShiftConfiguration[shiftConfigurationId] || []
  }

  filterByShiftConfigurations(shiftConfigurationIds: string[]): PlannedBreak[] {
    return shiftConfigurationIds.flatMap((id) => this.filterByShiftConfiguration(id))
  }
}

const PlannedBreaksContext = createContext<PlannedBreaks>(new PlannedBreaks([]))
const usePlannedBreaks = () => useContext(PlannedBreaksContext)

const PlannedBreaksProvider = ({
  collection,
  children,
}: PropsWithChildren<{ collection: PlannedBreaks }>) => {
  return (
    <PlannedBreaksContext.Provider value={collection}>{children}</PlannedBreaksContext.Provider>
  )
}

export default PlannedBreaks
export { PlannedBreaksProvider, usePlannedBreaks }
